import type { StateFrom } from 'xstate';
import { assign, fromPromise, setup } from 'xstate';
import type {
  IAvailabilityOptionDtoData,
  ICartPositionAvailabilityOptionsDtoData,
  ICartResponseDtoData,
  IExpirationReasonData,
  IOrderCurrentDTOData
} from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { api } from '~/features/cart/state-machine/backend-access/api';
import type { MakeOptional } from '~/features/cart/state-machine/types';
import { isLoginExpiredError, normalizeCartError } from '~/features/cart/state-machine/cartError';

export type UserOptionSelectedEvent = {
  type: 'userOptionSelected';
  selectedUserDialogOption: IAvailabilityOptionDtoData[];
};

export type PositionAvailabilityMachineState = StateFrom<typeof positionAvailabilityWorkflow>;

export const positionAvailabilityWorkflow = setup({
  types: {
    context: {} as {
      positionAvailabilityOptions: ICartPositionAvailabilityOptionsDtoData[] | undefined;
      selectedUserDialogOption: IAvailabilityOptionDtoData[] | undefined;
      currentOrder: IOrderCurrentDTOData | undefined;
      error: unknown;
      expirationReason: IExpirationReasonData | undefined;
    },
    events: {} as UserOptionSelectedEvent,
    input: {} as ICartPositionAvailabilityOptionsDtoData[],
    output: {} as MakeOptional<ICartResponseDtoData>
  },
  actors: {
    processPositionCommand: fromPromise(async ({ input }: { input: IAvailabilityOptionDtoData[] | undefined }) => {
      if (!input || input.length <= 0) {
        throw new Error('No availability options provided!');
      }
      const commands = input.map(option => option.command);
      const commandResponse = await api().sendPositonCommands(commands);
      return commandResponse;
    })
  }
}).createMachine({
  id: 'positionAvailability',
  context: ({ input }) => {
    return {
      positionAvailabilityOptions: input,
      selectedUserDialogOption: undefined,
      currentOrder: undefined,
      error: undefined,
      expirationReason: undefined
    };
  },
  output: ({ context }) => {
    const retVal = {
      order: context.currentOrder!,
      error: context.error,
      expirationReason: context.expirationReason!
    };
    return retVal;
  },
  initial: 'UserInputPositionOptions',
  states: {
    UserInputPositionOptions: {
      on: {
        userOptionSelected: {
          target: 'ProcessingPositionAvailabilityOption',
          actions: assign({
            selectedUserDialogOption: ({ event }) => event.selectedUserDialogOption
          })
        }
      }
    },
    ProcessingPositionAvailabilityOption: {
      invoke: {
        src: 'processPositionCommand',
        input: ({ context }) => context.selectedUserDialogOption,
        onDone: [
          {
            guard: ({ event }) =>
              event.output.positionAvailabilityOptions !== undefined &&
              event.output.positionAvailabilityOptions.length > 0,
            target: 'UserInputPositionOptions',
            actions: assign({
              selectedUserDialogOption: () => undefined,
              positionAvailabilityOptions: ({ event }) => event.output.positionAvailabilityOptions
            })
          },
          {
            target: 'ProcessedPositionAvailabilityOption',
            actions: assign({
              selectedUserDialogOption: () => undefined,
              positionAvailabilityOptions: () => undefined,
              expirationReason: ({ event }) => {
                return event.output.expirationReason;
              },
              currentOrder: ({ event }) => {
                return event.output.order;
              }
            })
          }
        ],
        onError: [
          {
            guard: ({ event }) => isLoginExpiredError(event.error),
            actions: ({ event }) => {
              // Login-Fehler (401/403) werden bewusst per throw an die übergeordnete State-Machine eskaliert,
              // damit deren invoke.onError greift (-> LoginExpired), statt wie andere Fehler über `output.error` zu laufen.
              throw normalizeCartError(event.error);
            }
          },
          {
            // Diese untergeordnete State-Machine geht in den finalen Zustand.
            // Die übergeordnete State-Machine wird dann über den `output.error` oder `output.expirationReason` den Fehlerzustand feststellen
            // und selbst in einen Fehlerzustand gehen
            target: 'ProcessedPositionAvailabilityOption',
            actions: assign({
              selectedUserDialogOption: () => undefined,
              positionAvailabilityOptions: () => undefined,
              currentOrder: () => undefined,
              error: ({ event }) => {
                return event.error;
              }
            })
          }
        ]
      }
    },
    ProcessedPositionAvailabilityOption: {
      type: 'final'
    }
  }
});
